import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Spinner,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  analyzeVideoMetadata,
  repairVideoMetadata,
  type VideoMetadataAnalysisResult,
  type VideoMetadataRepairResult,
} from '../services/mediaManagementService'

export const VideoMetadata: React.FC = () => {
  // Video Metadata Analysis state
  const [videoMetadataAnalysisData, setVideoMetadataAnalysisData] =
    useState<VideoMetadataAnalysisResult | null>(null)
  const [isAnalyzingVideoMetadata, setIsAnalyzingVideoMetadata] =
    useState(false)

  // Video Metadata Repair state
  const [videoMetadataRepairData, setVideoMetadataRepairData] =
    useState<VideoMetadataRepairResult | null>(null)
  const [isRepairingVideoMetadata, setIsRepairingVideoMetadata] =
    useState(false)
  const [videoRepairSettings, setVideoRepairSettings] = useState({
    dryRun: true,
    maxRepairs: 200,
  })

  const toast = useToast()

  const runVideoMetadataAnalysis = async () => {
    setIsAnalyzingVideoMetadata(true)
    try {
      const result = await analyzeVideoMetadata()
      setVideoMetadataAnalysisData(result)

      toast.success(
        `Analysis completed! Found ${result.totalMp4Files} MP4 files, ${result.filesMissingMetadata} need metadata.`
      )
    } catch (error) {
      console.error('Error analyzing video metadata:', error)
      toast.error(
        `Failed to analyze video metadata: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    } finally {
      setIsAnalyzingVideoMetadata(false)
    }
  }

  const runVideoMetadataRepair = async () => {
    setIsRepairingVideoMetadata(true)
    try {
      const result = await repairVideoMetadata({
        dryRun: videoRepairSettings.dryRun,
        maxRepairs: videoRepairSettings.maxRepairs,
      })
      setVideoMetadataRepairData(result)

      if (result.dryRun) {
        toast.success(
          `Dry run completed! Found ${result.repairsNeeded} MP4s that need dimension metadata.`
        )
      } else {
        toast.success(
          `Repair completed! Added dimensions to ${result.repairsCompleted} MP4s successfully.`
        )
      }
    } catch (error) {
      console.error('Error repairing video metadata:', error)
      toast.error(
        `Failed to repair video metadata: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    } finally {
      setIsRepairingVideoMetadata(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Video Metadata Analysis Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">Video Metadata Analysis</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Analyze all MP4 files to see which ones are missing video
                dimensions metadata
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onPress={runVideoMetadataAnalysis}
                isDisabled={isAnalyzingVideoMetadata}
                color="secondary"
                variant="flat"
                startContent={
                  isAnalyzingVideoMetadata ? (
                    <Spinner size="sm" />
                  ) : (
                    <span>🔍</span>
                  )
                }
              >
                {isAnalyzingVideoMetadata
                  ? 'Analyzing...'
                  : 'Analyze Video Metadata'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Analysis Results */}
      {videoMetadataAnalysisData && (
        <Card>
          <CardHeader>
            <h4 className="text-lg font-semibold">Analysis Results</h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {videoMetadataAnalysisData.totalMp4Files}
                  </div>
                  <div className="text-sm text-slate-500">Total MP4 Files</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {videoMetadataAnalysisData.filesWithMetadata}
                  </div>
                  <div className="text-sm text-slate-500">
                    Files with Metadata
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {videoMetadataAnalysisData.filesMissingMetadata}
                  </div>
                  <div className="text-sm text-slate-500">Missing Metadata</div>
                </div>
              </div>

              {/* Recommendations */}
              {videoMetadataAnalysisData.recommendations.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-semibold">Recommendations:</h5>
                  <div className="space-y-1">
                    {videoMetadataAnalysisData.recommendations.map(
                      (recommendation, index) => (
                        <div
                          key={index}
                          className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-2 rounded"
                        >
                          {recommendation}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Examples of Missing Files */}
              {videoMetadataAnalysisData.metadataStatus.missing.examples
                .length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-semibold">
                    Files Missing Metadata (showing first 10):
                  </h5>
                  <div className="max-h-48 overflow-y-auto">
                    <Table aria-label="Files missing metadata">
                      <TableHeader>
                        <TableColumn>GIF ID</TableColumn>
                        <TableColumn>File Name</TableColumn>
                        <TableColumn>File Size</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {videoMetadataAnalysisData.metadataStatus.missing.examples.map(
                          (file) => (
                            <TableRow key={file.gifId}>
                              <TableCell>
                                <span className="font-mono text-sm">
                                  {file.gifId}
                                </span>
                              </TableCell>
                              <TableCell>{file.fileName}</TableCell>
                              <TableCell>
                                {file.fileSizeBytes > 0
                                  ? `${(file.fileSizeBytes / 1024 / 1024).toFixed(1)} MB`
                                  : 'Unknown'}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Video Metadata Repair Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">Video Dimensions Repair</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Scan MP4 files and add missing video dimensions metadata for
                better Discord auto-play embedding
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onPress={runVideoMetadataRepair}
                isDisabled={isRepairingVideoMetadata}
                color="primary"
                variant="flat"
                startContent={
                  isRepairingVideoMetadata ? (
                    <Spinner size="sm" />
                  ) : (
                    <span>🔧</span>
                  )
                }
              >
                {isRepairingVideoMetadata
                  ? 'Processing...'
                  : 'Repair Video Metadata'}
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Repair Settings */}
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Checkbox
                isSelected={videoRepairSettings.dryRun}
                onValueChange={(checked) =>
                  setVideoRepairSettings((prev) => ({
                    ...prev,
                    dryRun: checked,
                  }))
                }
              >
                Dry Run Mode - Test without making changes
              </Checkbox>
            </div>
            <div>
              <Input
                type="number"
                min="1"
                max="500"
                label="Max Repairs"
                value={videoRepairSettings.maxRepairs.toString()}
                onChange={(e) =>
                  setVideoRepairSettings((prev) => ({
                    ...prev,
                    maxRepairs: Math.max(
                      1,
                      Math.min(500, parseInt(e.target.value) || 1)
                    ),
                  }))
                }
                size="sm"
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Results Display */}
      {videoMetadataRepairData && (
        <Card>
          <CardHeader>
            <h4 className="text-lg font-semibold">
              {videoMetadataRepairData.dryRun
                ? 'Repair Preview'
                : 'Repair Results'}
            </h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {videoMetadataRepairData.totalProcessed}
                  </div>
                  <div className="text-xs text-slate-500">Total Processed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                    {videoMetadataRepairData.repairsNeeded}
                  </div>
                  <div className="text-xs text-slate-500">Need Repair</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {videoMetadataRepairData.repairsCompleted}
                  </div>
                  <div className="text-xs text-slate-500">
                    {videoMetadataRepairData.dryRun ? 'Would Fix' : 'Fixed'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">
                    {videoMetadataRepairData.repairsFailed}
                  </div>
                  <div className="text-xs text-slate-500">Failed</div>
                </div>
              </div>

              {/* Detailed Results */}
              {videoMetadataRepairData.results.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-semibold">Detailed Results:</h5>
                  <div className="max-h-64 overflow-y-auto">
                    <Table aria-label="Video metadata repair results">
                      <TableHeader>
                        <TableColumn>GIF ID</TableColumn>
                        <TableColumn>Status</TableColumn>
                        <TableColumn>Dimensions</TableColumn>
                        <TableColumn>Processing Time</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {videoMetadataRepairData.results.map((result) => (
                          <TableRow key={result.gifId}>
                            <TableCell>
                              <span className="font-mono text-sm">
                                {result.gifId}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="sm"
                                color={
                                  result.status === 'success'
                                    ? 'success'
                                    : result.status === 'skipped'
                                      ? 'warning'
                                      : 'danger'
                                }
                                variant="flat"
                              >
                                {result.status}
                              </Chip>
                            </TableCell>
                            <TableCell>
                              {result.dimensions
                                ? `${result.dimensions.width}×${result.dimensions.height}`
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {result.processingTimeMs
                                ? `${result.processingTimeMs}ms`
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default VideoMetadata
